import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  Chat as ChatIcon,
  CalendarMonth as AvailIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Business } from '../types';
import { Order } from '../types/cart';
import { CHA } from './ChaaviVendorDashboardShell';

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sec = Math.round((Date.now() - d.getTime()) / 1000);
  if (sec < 45) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} mins ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hrs ago`;
  if (sec < 172800) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function categoryEmoji(b: Business): string {
  const c = (b.businessCategory || '').toLowerCase();
  if (c.includes('tent')) return '⛺';
  if (c.includes('cater')) return '🍽️';
  if (c.includes('farm')) return '🏡';
  return '🏪';
}

function formatCategoryLabel(b: Business): string {
  const c = b.businessCategory || '';
  return c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, ' ');
}

export interface ActivityRow {
  id: string;
  kind: 'msg' | 'order' | 'review';
  title: string;
  time: string;
  onClick?: () => void;
}

interface ChaaviVendorOverviewHomeProps {
  businesses: Business[];
  selectedBusiness: Business | null;
  orders: Order[];
  unreadChatCount: number;
  vendorNotifications: Array<{ notificationId?: string; message?: string; title?: string; createdAt?: string }>;
  ratingAvg: number | null;
  ratingCount: number;
  onSeeAllBusinesses: () => void;
  onEditBusiness: () => void;
  onManageInventory: () => void;
  onViewDetails: () => void;
  onMessages: () => void;
  onAvailability: () => void;
  onReviews: () => void;
}

const ChaaviVendorOverviewHome: React.FC<ChaaviVendorOverviewHomeProps> = ({
  businesses,
  selectedBusiness,
  orders,
  unreadChatCount,
  vendorNotifications,
  ratingAvg,
  ratingCount,
  onSeeAllBusinesses,
  onEditBusiness,
  onManageInventory,
  onViewDetails,
  onMessages,
  onAvailability,
  onReviews,
}) => {
  const now = Date.now();
  const weekAgo = now - 7 * 86400000;
  const notifThisWeek = vendorNotifications.filter((n) => {
    const t = n.createdAt ? new Date(n.createdAt).getTime() : 0;
    return t >= weekAgo;
  }).length;

  const activeBookings = orders.filter(
    (o) =>
      o.status !== 'CANCELLED' &&
      o.status !== 'DELIVERED' &&
      ['PENDING', 'CONFIRMED', 'PREPARING', 'READY'].includes(o.status)
  ).length;

  const upcoming = orders.filter((o) => {
    if (o.status === 'CANCELLED' || o.status === 'DELIVERED') return false;
    const t = o.deliveryDate ? new Date(o.deliveryDate).getTime() : 0;
    return t > now;
  }).length;

  const enquiriesCount = vendorNotifications.length;
  const pendingReplies = unreadChatCount;

  const kpis = [
    {
      label: 'Total enquiries',
      val: String(enquiriesCount),
      sub: notifThisWeek > 0 ? `+${notifThisWeek} this week` : 'No new this week',
      subAccent: notifThisWeek > 0 ? 'success' as const : 'muted' as const,
    },
    {
      label: 'Active bookings',
      val: String(activeBookings),
      sub: upcoming > 0 ? `${upcoming} upcoming` : 'None upcoming',
      subAccent: upcoming > 0 ? 'success' as const : 'muted' as const,
    },
    {
      label: 'Avg. rating',
      val: ratingAvg != null && ratingCount > 0 ? `${ratingAvg.toFixed(1)} ★` : '—',
      sub: ratingCount > 0 ? `from ${ratingCount} review${ratingCount === 1 ? '' : 's'}` : 'Not yet rated',
      subAccent: 'muted' as const,
    },
    {
      label: 'Pending replies',
      val: String(pendingReplies),
      sub: pendingReplies > 0 ? 'needs attention' : 'All caught up',
      subAccent: pendingReplies > 0 ? 'warn' as const : 'muted' as const,
    },
  ];

  const activityRows: ActivityRow[] = [];
  for (const n of vendorNotifications.slice(0, 5)) {
    const msg = (n.message || n.title || 'Notification').trim();
    const time = n.createdAt ? formatRelativeTime(n.createdAt) : '';
    activityRows.push({
      id: n.notificationId || `n-${msg}-${time}`,
      kind: 'order',
      title: msg.length > 72 ? `${msg.slice(0, 70)}…` : msg,
      time,
    });
  }
  if (activityRows.length === 0 && unreadChatCount > 0) {
    activityRows.push({
      id: 'chat-hint',
      kind: 'msg',
      title: 'You have unread client messages',
      time: 'Open Messages to reply',
    });
  }
  if (activityRows.length === 0) {
    activityRows.push({
      id: 'empty',
      kind: 'order',
      title: 'No recent activity yet',
      time: 'Orders and alerts will show up here',
    });
  }

  const dotColor = (k: ActivityRow['kind']) => {
    if (k === 'msg') return CHA.accent;
    if (k === 'order') return '#4a9e6a';
    return '#c08020';
  };

  const qcards = [
    {
      title: 'Messages',
      sub: 'Chat with clients',
      icon: ChatIcon,
      iconBg: '#fdf0e0',
      iconColor: CHA.accent,
      badge: unreadChatCount > 0 ? `${unreadChatCount} unread` : undefined,
      onClick: onMessages,
    },
    {
      title: 'Availability',
      sub: 'Set open dates',
      icon: AvailIcon,
      iconBg: '#fef4e0',
      iconColor: '#c08020',
      onClick: onAvailability,
    },
    {
      title: 'Reviews',
      sub: 'See client feedback',
      icon: StarIcon,
      iconBg: '#fdecea',
      iconColor: '#b03a2a',
      onClick: onReviews,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' },
          gap: 1.5,
          mb: 2.75,
        }}
      >
        {kpis.map((k) => (
          <Box
            key={k.label}
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: `0.5px solid ${CHA.border}`,
              p: '14px 16px',
            }}
          >
            <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
              {k.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 500,
                color: k.label === 'Pending replies' && pendingReplies > 0 ? CHA.accent : CHA.ink,
                fontFamily: 'sans-serif',
                lineHeight: 1,
              }}
            >
              {k.val}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: CHA.muted,
                fontFamily: 'sans-serif',
                mt: 0.5,
                ...(k.subAccent === 'success' ? { color: '#4a9e6a' } : {}),
                ...(k.subAccent === 'warn' ? { color: CHA.accent } : {}),
              }}
            >
              {k.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.3fr) minmax(0, 1fr)' },
          gap: 1.75,
          mb: 2.75,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
              My businesses ({businesses.length})
            </Typography>
            <Button
              disableRipple
              onClick={onSeeAllBusinesses}
              sx={{ fontSize: 12, color: CHA.accent, textTransform: 'none', minWidth: 0, p: 0 }}
            >
              See all
            </Button>
          </Box>

          {selectedBusiness ? (
            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.25, py: 2, pb: 1.5, borderBottom: '0.5px solid #f5f0e8', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: '#fdf0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    flexShrink: 0,
                  }}
                >
                  {categoryEmoji(selectedBusiness)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                    {selectedBusiness.businessName}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: CHA.muted, fontFamily: 'sans-serif', mt: 0.25 }}>
                    {formatCategoryLabel(selectedBusiness)}
                    {selectedBusiness.businessDescription
                      ? ` · ${selectedBusiness.businessDescription.slice(0, 48)}${selectedBusiness.businessDescription.length > 48 ? '…' : ''}`
                      : ''}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    px: 1.1,
                    py: 0.4,
                    borderRadius: '10px',
                    bgcolor: selectedBusiness.isActive ? '#e8f4ed' : '#f0f0f0',
                    color: selectedBusiness.isActive ? '#3a7a50' : CHA.muted,
                    fontFamily: 'sans-serif',
                    flexShrink: 0,
                  }}
                >
                  {selectedBusiness.isActive ? 'Active' : 'Inactive'}
                </Box>
              </Box>
              <Box sx={{ px: 2.25, py: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif' }}>Phone</Typography>
                  <Typography sx={{ fontSize: 13, color: CHA.ink, fontFamily: 'sans-serif' }}>{selectedBusiness.businessPhone || '—'}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif' }}>Email</Typography>
                  <Typography sx={{ fontSize: 12, color: CHA.ink, fontFamily: 'sans-serif', wordBreak: 'break-all' }}>
                    {selectedBusiness.businessEmail || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif' }}>Category</Typography>
                  <Typography sx={{ fontSize: 13, color: CHA.ink, fontFamily: 'sans-serif' }}>{formatCategoryLabel(selectedBusiness)}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif' }}>Listed since</Typography>
                  <Typography sx={{ fontSize: 13, color: CHA.ink, fontFamily: 'sans-serif' }}>
                    {selectedBusiness.createdAt ? new Date(selectedBusiness.createdAt).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ px: 2.25, py: 1.25, borderTop: '0.5px solid #f5f0e8', bgcolor: '#fdfaf5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  onClick={onViewDetails}
                  sx={{
                    flex: 1,
                    minWidth: 90,
                    py: 0.9,
                    borderRadius: '7px',
                    border: '0.5px solid #d6cfc0',
                    bgcolor: '#fff',
                    color: '#5a4a35',
                    fontSize: 12,
                    textTransform: 'none',
                    fontFamily: 'sans-serif',
                    '&:hover': { bgcolor: '#f5f0e8' },
                  }}
                >
                  View details
                </Button>
                <Button
                  onClick={onManageInventory}
                  sx={{
                    flex: 1,
                    minWidth: 90,
                    py: 0.9,
                    borderRadius: '7px',
                    border: '0.5px solid #d6cfc0',
                    bgcolor: '#fff',
                    color: '#5a4a35',
                    fontSize: 12,
                    textTransform: 'none',
                    fontFamily: 'sans-serif',
                    '&:hover': { bgcolor: '#f5f0e8' },
                  }}
                >
                  Manage inventory
                </Button>
                <Button
                  onClick={onEditBusiness}
                  sx={{
                    flex: 1,
                    minWidth: 90,
                    py: 0.9,
                    borderRadius: '7px',
                    border: 'none',
                    bgcolor: CHA.accentHover,
                    color: '#fff',
                    fontSize: 12,
                    textTransform: 'none',
                    fontFamily: 'sans-serif',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: CHA.accent, color: '#fff', boxShadow: 'none' },
                  }}
                >
                  Edit business
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, p: 3 }}>
              <Typography sx={{ color: CHA.muted, fontFamily: 'sans-serif', fontSize: 14 }}>Select a business above to see its card.</Typography>
            </Box>
          )}
        </Box>

        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif', mb: 1.5 }}>Quick access</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 2.25 }}>
            {qcards.map((q) => {
              const Icon = q.icon;
              return (
                <Box
                  key={q.title}
                  onClick={q.onClick}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    border: `0.5px solid ${CHA.border}`,
                    p: '14px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    cursor: 'pointer',
                    transition: 'border-color 0.12s',
                    '&:hover': { borderColor: CHA.accent },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '8px',
                      bgcolor: q.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 16, color: q.iconColor }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>{q.title}</Typography>
                    <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif', mt: 0.25 }}>{q.sub}</Typography>
                    {q.badge && (
                      <Typography
                        component="span"
                        sx={{
                          display: 'inline-block',
                          mt: 0.5,
                          bgcolor: CHA.accent,
                          color: '#fff',
                          fontSize: 10,
                          px: 0.75,
                          py: 0.125,
                          borderRadius: '8px',
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {q.badge}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif', mb: 1.5 }}>Recent activity</Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, overflow: 'hidden' }}>
            {activityRows.map((row, i) => (
              <Box
                key={row.id}
                onClick={row.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderBottom: i < activityRows.length - 1 ? '0.5px solid #f5f0e8' : 'none',
                  cursor: row.onClick ? 'pointer' : 'default',
                  '&:hover': { bgcolor: row.onClick ? 'rgba(58,46,30,0.04)' : undefined },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor(row.kind), flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: CHA.ink, fontFamily: 'sans-serif' }}>{row.title}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#c0b8a8', fontFamily: 'sans-serif', mt: 0.125 }}>{row.time}</Typography>
                </Box>
                <Typography sx={{ color: '#d6cfc0', fontSize: 14 }}>›</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChaaviVendorOverviewHome;
